import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AppUpdateService } from 'src/app/core/services/app-update.service';
import { DsButtonComponent } from '../ds-button/ds-button.component';

/**
 * Dải nhắc "Đã có bản mới · Tải lại", gắn thẳng vào app.component.html nên sống ở mọi
 * màn hình (kể cả shell mobile) và không phụ thuộc route nào.
 *
 * Không dùng `ToastService` được: toast của dự án tự tắt sau 5 giây, không có nút hành
 * động, và neo ở góc trên bên phải với bề ngang cố định 320px — trên điện thoại nó đè
 * lên header của shell mobile. Lời nhắc cập nhật thì phải chờ được người dùng bấm.
 */
@Component({
  selector: 'app-update-prompt',
  standalone: true,
  imports: [TranslateModule, DsButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (svc.state(); as state) {
      @if (state !== 'none') {
        <!-- pointer-events: lớp phủ trải hết bề ngang chỉ để căn giữa, nên phải cho chuột
             xuyên qua, riêng thẻ bên trong nhận lại sự kiện. Không có nó thì dải này chắn
             mất cả một vệt ngang màn hình. -->
        <div
          class="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-3 pt-3"
          style="padding-bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px))"
        >
          <div
            class="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-lg border border-warm-border bg-warm-card px-4 py-3 shadow-lg"
          >
            <div class="min-w-0 flex-1">
              <p class="text-token-body font-semibold text-warm-text">
                {{ titleKey() | translate }}
              </p>
              <p class="text-token-caption text-warm-secondary">
                {{ messageKey() | translate }}
              </p>
            </div>
            @if (state === 'ready') {
              <ds-button variant="ghost" size="sm" (click)="svc.dismiss()">
                {{ 'AppUpdate.Later' | translate }}
              </ds-button>
            }
            <ds-button variant="primary" size="sm" (click)="reload()">
              {{ 'AppUpdate.Reload' | translate }}
            </ds-button>
          </div>
        </div>
      }
    }
  `,
})
export class AppUpdatePromptComponent {
  protected readonly svc = inject(AppUpdateService);

  protected readonly titleKey = computed(() =>
    this.svc.state() === 'broken' ? 'AppUpdate.BrokenTitle' : 'AppUpdate.Title',
  );

  protected readonly messageKey = computed(() =>
    this.svc.state() === 'broken' ? 'AppUpdate.BrokenMessage' : 'AppUpdate.Message',
  );

  protected reload(): void {
    void this.svc.reload();
  }
}
